// The doorway grammar (#11426): a quiet action that takes you to the rest
// of something — "View all", "Show all", "12 more". Promoted from
// OverviewLayout once the Relationships tab became the second surface to
// need it.
//
// Two shapes. The default is inline quiet ink text, for a doorway living
// inside a sentence. `variant="action"` is a card's exit — the same quiet
// bordered treatment its header actions establish (design round 6): bare
// link text at a card's foot read as a stray sentence rather than the
// card's exit.
//
// On a page drawn for looking (the Appearance editor's sample record, #13370
// round 41) a doorway would act on the record, so it is drawn disabled, with
// the design system's disabled look (Btn's): faded, the not-allowed cursor.

import React from "react";
import { WITHHELD_LOOK } from "./Button";
import { StillPageContext } from "./stillPage";

export const Doorway: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "text" | "action";
}> = ({ onClick, children, variant = "text" }) => {
  const still = React.useContext(StillPageContext);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={still}
      style={{
        ...doorwayStyle(variant),
        ...(still ? WITHHELD_LOOK : {}),
      }}
    >
      {children}
    </button>
  );
};

const doorwayStyle = (variant: "text" | "action"): React.CSSProperties =>
  variant === "action"
    ? {
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--fp-gap-hair)",
        background: "none",
        border: "1px solid var(--fp-border-strong)",
        // Btn's radius — the action doorway must read as that family.
        borderRadius: "7px",
        padding: "var(--fp-gap-hair) var(--fp-gap-tight)",
        cursor: "pointer",
        fontFamily: "var(--fp-font-ui)",
        fontSize: "var(--fp-t-meta)",
        fontWeight: "var(--fp-w-primary)" as unknown as number,
        color: "var(--fp-ink-2)",
      }
    : {
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        fontFamily: "var(--fp-font-ui)",
        fontSize: "var(--fp-t-meta)",
        // Ink, not the accent (#11426 component sweep, D6): on a token
        // surface a quiet text control is ink — the accent blue made
        // the disclosure the loudest thing on the card.
        color: "var(--fp-ink-2)",
        textDecorationLine: "underline",
        textDecorationColor: "var(--fp-border-strong)",
        textUnderlineOffset: "3px",
      };
